const GOOGLE_CSE_URL = "https://www.googleapis.com/customsearch/v1";

/** Set after Google returns 403 "project does not have access" so we stop retrying. */
let cseAccessDenied: string | null = null;

export function getGoogleCseId(): string {
	return (process.env.GOOGLE_CSE_ID ?? "").trim();
}

export function getGoogleCseApiKey(): string {
	return (
		process.env.GOOGLE_CSE_API_KEY?.trim() ||
		process.env.GOOGLE_SEARCH_API_KEY?.trim() ||
		""
	);
}

export function hasGoogleCseConfig(): boolean {
	return Boolean(getGoogleCseId() && getGoogleCseApiKey()) && !cseAccessDenied;
}

function customSearchApiDeniedMessage(detail: string): string | null {
	if (!/does not have the access to Custom Search JSON API/i.test(detail))
		return null;
	return [
		"Google Custom Search JSON API is not enabled for the Cloud project that owns GOOGLE_CSE_API_KEY.",
		"Enable it at https://console.cloud.google.com/apis/library/customsearch.googleapis.com",
		"then create (or restrict) an API key in that same project.",
		"A Gemini / AI Studio key is not enough even if GOOGLE_CSE_ID is set.",
	].join(" ");
}

export interface GoogleCseImageHit {
	url: string;
	contextUrl?: string;
	title?: string;
	width?: number;
	height?: number;
}

function asRecord(value: unknown): Record<string, unknown> | null {
	if (!value || typeof value !== "object" || Array.isArray(value)) return null;
	return value as Record<string, unknown>;
}

/**
 * Official Google Programmable Search image results (not a scrape).
 * Requires GOOGLE_CSE_ID + GOOGLE_CSE_API_KEY.
 */
export async function searchGoogleCseImages(
	query: string,
	limit = 6,
): Promise<GoogleCseImageHit[]> {
	const q = query.trim();
	if (!q || !hasGoogleCseConfig()) return [];
	if (cseAccessDenied) return [];

	const url = new URL(GOOGLE_CSE_URL);
	url.searchParams.set("key", getGoogleCseApiKey());
	url.searchParams.set("cx", getGoogleCseId());
	url.searchParams.set("q", q.slice(0, 200));
	url.searchParams.set("searchType", "image");
	url.searchParams.set("num", String(Math.min(10, Math.max(1, limit))));
	url.searchParams.set("safe", "active");

	const response = await fetch(url.toString(), {
		headers: { Accept: "application/json" },
	});
	if (!response.ok) {
		const detail = await response.text().catch(() => "");
		const denied = customSearchApiDeniedMessage(detail);
		if (denied) {
			cseAccessDenied = denied;
			throw new Error(denied);
		}
		throw new Error(
			`Google CSE image search failed (${response.status})${detail ? `: ${detail.slice(0, 240)}` : ""}`,
		);
	}

	const payload = asRecord(await response.json());
	const items = Array.isArray(payload?.items) ? payload.items : [];
	const hits: GoogleCseImageHit[] = [];
	for (const item of items) {
		const row = asRecord(item);
		const imageUrl = typeof row?.link === "string" ? row.link.trim() : "";
		if (!imageUrl.startsWith("http")) continue;
		const image = asRecord(row?.image);
		hits.push({
			url: imageUrl,
			contextUrl:
				typeof image?.contextLink === "string"
					? image.contextLink.trim()
					: undefined,
			title: typeof row?.title === "string" ? row.title.trim() : undefined,
			width:
				typeof image?.width === "number"
					? image.width
					: Number.parseInt(String(image?.width ?? ""), 10) || undefined,
			height:
				typeof image?.height === "number"
					? image.height
					: Number.parseInt(String(image?.height ?? ""), 10) || undefined,
		});
	}
	return hits;
}
